import React, { useContext, useState } from 'react';
import {
  Button,
  Card,
  Icon,
  IconButton,
  InlineExternalLink,
  LoadingIndicator,
  TextField,
} from '@monash/portal-react';
import { Controller, useFieldArray } from 'react-hook-form';
import c from './category-form.module.scss';
import { INLINE_ERROR_MSGS } from '../../../../constants/inline-error-messages';
import { nanoid } from 'nanoid';
import { DndContext } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import SortableItem from '../sortable-item/SortableItem';
import { SlideOutFormContext } from '../../../common/slide-out/SlideOutForm';
import { APIContext } from '@monash/portal-frontend-common';
import { useSnackbar } from '../../../providers/SnackbarProvider';

const Subcategories = ({
  control,
  getValues,
  errors,
  links,
  errorRow,
  refreshData,
  setHistory,
}) => {
  const { openActionModal, isActionModalOpen } =
    useContext(SlideOutFormContext);
  const { deleteSubcategory } = useContext(APIContext);
  const { addSnackbar } = useSnackbar();

  const [loadingDelete, setLoadingDelete] = useState(false);

  const {
    fields: subcategories,
    append: appendSubcatItem,
    remove: removeSubcatItem,
    move: moveSubcatItems,
  } = useFieldArray({
    name: 'subcategories',
    keyName: 'key',
    control,
  });

  const generateErrorMessage = (e) => {
    const statusFamily = Math.floor(e.status / 100);
    if (statusFamily === 4) {
      return (
        <>
          We’re having trouble deleting the subcategory - please{' '}
          <InlineExternalLink
            text="log a bug"
            link="https://admin.staff.monash/feedback"
          />
        </>
      );
    } else {
      return 'We’re having trouble deleting the sub category right now - please try again.';
    }
  };

  const handleSubcategoryDelete = async (index, inModal = false) => {
    try {
      setLoadingDelete(true);
      const values = getValues();

      // Only call API if subcategory already exists in DB
      if (values.id) {
        const response = await deleteSubcategory(
          values.id,
          values.subcategories[index].id
        );
        setHistory(response.history);
      }

      removeSubcatItem(index);
      refreshData();
      addSnackbar({
        message: `Sub-category "${values.subcategories[index].title}" has been deleted`,
        type: 'success',
      });
      return true;
    } catch (error) {
      if (inModal) {
        throw error;
      } else {
        const msg = generateErrorMessage(error);
        addSnackbar({
          message: msg,
          type: 'error',
        });
      }
    } finally {
      setLoadingDelete(false);
    }
  };

  const openSubcatRemovalModal = async (index, relevantLinks) => {
    if (relevantLinks.length) {
      const modalContent = (
        <div className={c.deleteModalContent}>
          <p>
            There are {relevantLinks.length} links associated with "
            {subcategories[index].title}" subcategory.
          </p>
          <div className={c.subcategoryDisplayList}>
            {relevantLinks.map((link) => (
              <Card key={link.id} className={c.subcategoryItem}>
                {link.name}
              </Card>
            ))}
          </div>
          <p>
            These links will only be visible on the All links page when the
            subcategory is deleted.
          </p>
        </div>
      );
      openActionModal({
        title: 'Delete subcategory',
        content: modalContent,
        cta: {
          onClick: () => handleSubcategoryDelete(index, true),
          onError: generateErrorMessage,
          async: true,
          variant: 'delete',
          label: 'Delete',
        },
      });
    } else {
      handleSubcategoryDelete(index, false);
    }
  };

  const isSubcategoryUnique = (title) => {
    const values = getValues();
    const existing = values.subcategories?.filter((sub) => sub.title === title);
    return existing.length === 1;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    const values = getValues();

    if (active.id !== over.id) {
      const oldIndex = values.subcategories.findIndex(
        (subcat) => subcat.id === active.id
      );
      const newIndex = values.subcategories.findIndex(
        (subcat) => subcat.id === over.id
      );
      moveSubcatItems(oldIndex, newIndex);
    }
  };

  return (
    <div className={`${c.subcategories} ${loadingDelete ? c.disabled : ''}`}>
      {loadingDelete && !isActionModalOpen && (
        <div className={c.loadingBlocker}>
          <LoadingIndicator />
        </div>
      )}
      <div className={c.subHeader}>
        <h3>Subcategories</h3>
        <Button
          onClick={() => appendSubcatItem({ id: nanoid(), title: '' })}
          variant="text"
          size="small"
          icon={<Icon.Plus />}
        >
          New subcategory
        </Button>
      </div>

      <DndContext
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={subcategories.map((subcat) => subcat.id)}
          strategy={verticalListSortingStrategy}
        >
          {subcategories.map((subcategory, index) => {
            const relevantLinks =
              links?.filter((link) =>
                link.categories.some((cat) =>
                  cat.subcategories.some((sub) => {
                    return sub.id === subcategory.id;
                  })
                )
              ) || [];
            return (
              <SortableItem
                key={subcategory.key}
                id={subcategory.id}
                conditionalContent={errorRow([
                  errors.subcategories?.[index]?.title,
                ])}
              >
                <div className={c.subcategoryItem}>
                  <Controller
                    control={control}
                    name={`subcategories.${index}.title`}
                    rules={{
                      required: INLINE_ERROR_MSGS.SUB_CATEGORY,
                      validate: (val) => {
                        if (!isSubcategoryUnique(val)) {
                          return INLINE_ERROR_MSGS.SUB_CATEGORY_UNIQUE;
                        }
                      },
                    }}
                    render={({ field: { onBlur, onChange, value } }) => (
                      <div className={c.field}>
                        <TextField
                          onBlur={onBlur}
                          onChange={onChange}
                          value={value}
                          placeholder="Subcategory"
                        />
                      </div>
                    )}
                  />
                  <div>{relevantLinks.length} links</div>
                  <IconButton
                    onClick={() => openSubcatRemovalModal(index, relevantLinks)}
                    icon={Icon.Trash}
                    color="var(--card-text-color)"
                    size={24}
                    aria-label="Delete subcategory"
                    disabled={loadingDelete}
                  />
                </div>
              </SortableItem>
            );
          })}
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default Subcategories;
